import { resolvePortalMessage } from "../../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Documents — `/finance/documents` (plan-014 §4, design §27.15.1; FR12-DOC-01…06). Built by
 * plan-014 unit **D5b**, then **D6**; D5a owns `legal-profile-form.tsx`, mounted here above the table.
 *
 * D6 (FR12-DOC-05): the organization finance overview (`../overview/finance-overview.tsx`: settled, pending and estimated
 * apart, with the calculation instant and the billing timezone) above the legal details; the filters the documents
 * list takes — period, workspace, kind, payment state, balance — kept in the address bar and shared with the overview
 * (period, workspace, balance); and "Export CSV" (the prototype's button) over the same filters, one audited file.
 *
 * Drawn to the prototype's `docs` page (`design/WeTalk.dc.html`, the Organization block renderer): the legal name as
 * kicker, "Invoices, receipts and statements", the lede, the Finance tab strip, then two blocks — the legal and tax
 * details form (D5a's `LegalProfileForm`) and the documents table (Number, Kind, Period, Workspace, Amount, State,
 * PDF), with the month in progress as "Not issued yet". Styles are the shared Organization renderer's
 * (`../../account-admin/page/organization-page.css`) plus `./financial-document-screen.css`, tokens only (ADR 0018).
 *
 * EVERY STATE IS REAL (FR12-SCR-04): loading, a failure in the API's words, empty ("No documents yet"), documents
 * withheld while WeTalk's issuer details are not set (explained, never an unexplained empty table), a document awaiting
 * the tax authority (no PDF yet), a download (a 15-minute link, audited), a resend and its outcome. The server decides
 * everything; this page only shows what it answered.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Icon, Select } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type {
  EstimatedDocumentView,
  FinancialDocumentKindView,
  FinancialDocumentPaymentStateView,
  FinancialDocumentView,
} from "../../../api/slice/financial-document.js";
import { formatMoney } from "../../../money.js";
import { GLYPH } from "../../../shell/glyph.js";
import { accountProfileKey, roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { Block, BlockNote, Notice, type Outcome } from "../../account-admin/page/organization-page.js";
import { FinanceOverviewBlock, monthWordOf } from "../overview/finance-overview.js";
import { LegalProfileForm } from "./legal-profile-form.js";

import "./financial-document-screen.css";
import { FinanceTabs } from "../finance-tabs.js";

/**
 * The page's search parameters (the address bar is untrusted): the document kind, and (plan-014 D6) the period
 * (`YYYY-MM`, a billing month), the workspace, the balance and the payment state. Period, workspace and balance also
 * narrow the overview.
 */
export interface FinancialDocumentSearch {
  readonly kind?: FinancialDocumentKindView;
  readonly period?: string;
  readonly workspace?: string;
  readonly balance?: string;
  readonly payment?: FinancialDocumentPaymentStateView;
}

const KIND_SEARCH: readonly FinancialDocumentKindView[] = ["invoice", "consumption_statement", "credit_note", "payment_receipt"];
const PAYMENT_SEARCH: readonly FinancialDocumentPaymentStateView[] = ["unpaid", "partially_paid", "paid", "credited", "not_applicable"];
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const PERIOD_SHAPE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The route's `validateSearch`: a total function, so a hand-edited URL still opens a valid page. */
export function financialDocumentSearchOf(raw: Readonly<Record<string, unknown>>): FinancialDocumentSearch {
  const kind = KIND_SEARCH.find((one) => one === raw["kind"]);
  const payment = PAYMENT_SEARCH.find((one) => one === raw["payment"]);
  const text = (key: string, shape: RegExp): string | undefined => {
    const value = raw[key];
    return typeof value === "string" && shape.test(value) ? value : undefined;
  };
  const period = text("period", PERIOD_SHAPE);
  const workspace = text("workspace", UUID_SHAPE);
  const balance = text("balance", UUID_SHAPE);
  return {
    ...(kind === undefined ? {} : { kind }),
    ...(period === undefined ? {} : { period }),
    ...(workspace === undefined ? {} : { workspace }),
    ...(balance === undefined ? {} : { balance }),
    ...(payment === undefined ? {} : { payment }),
  };
}

/** The last twelve months as `YYYY-MM`, newest first (the period choices; the server decides the billing month). */
export function recentMonthsOf(now: Date): readonly string[] {
  const out: string[] = [];
  for (let back = 0; back < 12; back += 1) {
    const at = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 15));
    out.push(`${String(at.getUTCFullYear())}-${String(at.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export interface FinancialDocumentScreenProps {
  readonly search: FinancialDocumentSearch;
  readonly onSearch: (next: FinancialDocumentSearch) => void;
}

// ── copy ──────────────────────────────────────────────────────────────────────────────────────────────────────

export const FINANCIAL_DOCUMENT_COPY = {
  get kicker() { return portalText("finance", "m0515"); },
  get title() { return portalText("finance", "m1217"); },
  get lede() { return portalText("finance", "m1218"); },
  get tableTitle() { return portalText("finance", "m1219"); },
  get empty() { return portalText("finance", "m1220"); },
  get withheldTitle() { return portalText("finance", "m1221"); },
  get withheld() { return portalText("finance", "m1222"); },
  get noAccess() { return portalText("finance", "m1223"); },
  get chooseOrganization() { return portalText("finance", "m1224"); },
  exported: (count: number) => portalText("finance", "documents.exported", { count }),
  get exportFailed() { return portalText("finance", "m1225"); },
  get clear() { return portalText("finance", "m1226"); },
} as const;

const PAYMENT_OPTION: readonly { readonly value: "" | FinancialDocumentPaymentStateView; readonly label: string }[] = [
  { value: "", get label() { return portalText("finance", "m0427"); } },
  { value: "unpaid", get label() { return portalText("finance", "m0428"); } },
  { value: "partially_paid", get label() { return portalText("finance", "m0429"); } },
  { value: "paid", get label() { return portalText("finance", "m0430"); } },
  { value: "credited", get label() { return portalText("finance", "m0431"); } },
  { value: "not_applicable", get label() { return portalText("finance", "m0432"); } },
];

const KIND_WORD: Readonly<Record<FinancialDocumentKindView, string>> = Object.freeze({
  get invoice() { return portalText("finance", "m1145"); },
  get consumption_statement() { return portalText("finance", "m0448"); },
  get credit_note() { return portalText("finance", "m1146"); },
  get payment_receipt() { return portalText("finance", "m0299"); },
});

const KIND_OPTION: readonly { readonly value: "" | FinancialDocumentKindView; readonly label: string }[] = [
  { value: "", get label() { return portalText("finance", "m0433"); } },
  { value: "invoice", get label() { return portalText("finance", "m0214"); } },
  { value: "consumption_statement", get label() { return portalText("finance", "m0434"); } },
  { value: "credit_note", get label() { return portalText("finance", "m0435"); } },
  { value: "payment_receipt", get label() { return portalText("finance", "m0436"); } },
];

const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** The month a supply period covers, in its own timezone ("August"). */
export function periodWordOf(period: { readonly start: string; readonly timeZone: string }): string {
  if (portalLocale() === "el") return new Intl.DateTimeFormat("el-GR", { timeZone: period.timeZone, month: "long" }).format(new Date(period.start));
  const parts = new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { timeZone: period.timeZone, month: "numeric" }).formatToParts(new Date(period.start));
  const month = Number(parts.find((part) => part.type === "month")?.value);
  return MONTH[month - 1] ?? "";
}

/** The prototype's "20 Sep" for a document with no period (a top-up's invoice, a receipt). */
export function dayWordOf(iso: string): string {
  const at = new Date(iso);
  if (portalLocale() === "el") return new Intl.DateTimeFormat("el-GR", { day: "numeric", month: "short" }).format(at);
  return `${String(at.getDate())} ${MONTH_SHORT[at.getMonth()] ?? ""}`;
}

/** `€2,201.00`, a credit as `−€18.40` (a true minus, as the prototype prints it). */
export function amountWordOf(minor: number, currency: string): string {
  const text = formatMoney(money(Math.abs(minor), currency as CurrencyCode));
  return minor < 0 ? `−${text}` : text;
}

/** The State column: the document state, and for an invoice its payment. */
export function stateWordOf(document: FinancialDocumentView): { readonly text: string; readonly outline: boolean } {
  if (document.documentState === "awaiting_transmission") return { text: portalText("finance", "m0438"), outline: true };
  if (document.documentState === "corrected") return { text: portalText("finance", "m0439"), outline: true };
  if (document.kind === "invoice" || document.kind === "payment_receipt") {
    if (document.paymentState === "paid") return { text: portalText("finance", "m0430"), outline: false };
    if (document.paymentState === "partially_paid") return { text: portalText("finance", "m0429"), outline: true };
    if (document.paymentState === "unpaid") return { text: portalText("finance", "m0428"), outline: true };
  }
  return { text: portalText("finance", "m0440"), outline: document.kind === "credit_note" };
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────────

function FinanceHeader(props: { readonly outcome: Outcome | null }): ReactNode {
  usePortalTranslation("finance");
  return (
    <>
      <h1 className="op-title">{FINANCIAL_DOCUMENT_COPY.title}</h1>
      <p className="op-lede">{FINANCIAL_DOCUMENT_COPY.lede}</p>
      <FinanceTabs current="documents" />
      {props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
    </>
  );
}

function DocumentTable(props: {
  readonly document: readonly FinancialDocumentView[];
  readonly estimated: readonly EstimatedDocumentView[];
  readonly canResend: boolean;
  readonly busy: string | null;
  readonly onDownload: (document: FinancialDocumentView) => void;
  readonly onResend: (document: FinancialDocumentView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  return (
    <div className="op-table-wrap">
      <table className="table op-table fd-table">
        <thead>
          <tr>
            <th>{portalText("finance", "m0441")}</th>
            <th>{portalText("finance", "m0442")}</th>
            <th>{portalText("finance", "m0443")}</th>
            <th>{portalText("finance", "m0389")}</th>
            <th className="fd-right">{portalText("finance", "m0110")}</th>
            <th>{portalText("finance", "m0444")}</th>
            <th>
              <span className="fd-hidden">{portalText("finance", "m0445")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.document.map((one) => {
            const state = stateWordOf(one);
            return (
              <tr key={one.financialDocumentId}>
                <td className="fd-cell fd-number">{one.documentNumber ?? "—"}</td>
                <td className="fd-cell">{KIND_WORD[one.kind]}</td>
                <td className="fd-cell">{one.supplyPeriod === null ? dayWordOf(one.issuedAt) : periodWordOf(one.supplyPeriod)}</td>
                <td className="fd-cell">{one.billingAccountName ?? portalText("finance", "m0446")}</td>
                <td className={one.grossMinor < 0 ? "fd-cell fd-right fd-negative" : "fd-cell fd-right"}>{amountWordOf(one.grossMinor, one.currency)}</td>
                <td className="fd-cell">
                  <span className={state.outline ? "tag tag-outline fd-tag" : "tag tag-neutral fd-tag"}>{state.text}</span>
                </td>
                <td className="op-cell-action">
                  {one.downloadable ? (
                    <button type="button" className="op-link" disabled={props.busy === one.financialDocumentId} onClick={() => props.onDownload(one)}>{portalText("finance", "m0220")}</button>
                  ) : (
                    <span className="op-cell-dim">—</span>
                  )}
                  {props.canResend && one.documentState !== "awaiting_transmission" ? (
                    <button type="button" className="op-link" disabled={props.busy === one.financialDocumentId} onClick={() => props.onResend(one)}>{portalText("finance", "m0447")}</button>
                  ) : null}
                </td>
              </tr>
            );
          })}
          {props.estimated.map((one) => (
            <tr key={`estimated-${one.billingAccountId}`}>
              <td className="fd-cell fd-number fd-dim">—</td>
              <td className="fd-cell">{portalText("finance", "m0448")}</td>
              <td className="fd-cell">{periodWordOf(one.supplyPeriod)}</td>
              <td className="fd-cell">{one.billingAccountName ?? portalText("finance", "m0446")}</td>
              <td className="fd-cell fd-right fd-dim">{amountWordOf(one.netMinor, one.currency)}</td>
              <td className="fd-cell">
                <span className="tag tag-outline fd-tag">{portalText("finance", "m0449")}</span>
              </td>
              <td className="op-cell-action">
                <span className="op-cell-dim">—</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FinancialDocumentScreen(props: FinancialDocumentScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [cursor, setCursor] = useState<readonly (string | null)[]>([null]);
  const kind = props.search.kind ?? null;
  const period = props.search.period ?? null;
  const workspaceId = props.search.workspace ?? null;
  const billingAccountId = props.search.balance ?? null;
  const paymentState = props.search.payment ?? null;
  const at = cursor[cursor.length - 1] ?? null;
  const filter = { kind, period, workspaceId, billingAccountId, paymentState } as const;
  const filtered = kind !== null || period !== null || workspaceId !== null || billingAccountId !== null || paymentState !== null;

  const profile = useQuery({
    queryKey: accountProfileKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountProfile(accountId ?? ""),
    enabled: accountId !== null,
  });
  const balances = useQuery({
    queryKey: ["billing-account-list", accountId ?? "none"],
    queryFn: () => gateway.listBillingAccounts(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const page = useQuery({
    queryKey: ["financial-document", accountId ?? "none", kind ?? "all", period ?? "all", workspaceId ?? "all", billingAccountId ?? "all", paymentState ?? "all", at ?? "first"],
    queryFn: () => gateway.listFinancialDocuments(accountId ?? "", { ...filter, cursor: at }),
    // plan-014 review I-7: asked of everyone — a billing delegate of a dedicated balance (no organization role) is
    // answered its balance's documents; anyone else is refused, and the page says it is not theirs to read.
    enabled: accountId !== null,
    retry: false,
  });
  const download = useMutation({
    mutationFn: (document: FinancialDocumentView) => gateway.downloadFinancialDocument(document.financialDocumentId),
    onSuccess: (link) => {
      setOutcome({ tone: "done", text: () => (portalText("finance", "m0450", { value1: link.fileName })) });
      window.location.assign(link.url);
    },
    onError: (error) => setOutcome({ tone: "problem", text: () => (apiProblemText(error, portalText("finance", "m0451"))) }),
  });
  const resend = useMutation({
    mutationFn: (document: FinancialDocumentView) => gateway.resendFinancialDocument(document.financialDocumentId),
    onSuccess: (_nothing, document) => setOutcome({ tone: "done", text: () => (portalText("finance", "m0452", { value1: document.documentNumber ?? portalText("finance", "m1147") })) }),
    onError: (error) => setOutcome({ tone: "problem", text: () => (apiProblemText(error, portalText("finance", "m0453"))) }),
  });
  const exported = useMutation({
    mutationFn: () => gateway.exportFinancialDocuments(accountId ?? "", filter),
    onSuccess: (file) => {
      setOutcome({ tone: "done", text: () => (FINANCIAL_DOCUMENT_COPY.exported(file.rowCount)) });
      window.location.assign(file.downloadUrl);
    },
    onError: (error) => setOutcome({ tone: "problem", text: () => (apiProblemText(error, FINANCIAL_DOCUMENT_COPY.exportFailed)) }),
  });

  const organizationName = profile.data?.name ?? "";
  const onSearch = (next: FinancialDocumentSearch): void => {
    setCursor([null]);
    props.onSearch(next);
  };
  const withChange = <K extends keyof FinancialDocumentSearch>(key: K, value: FinancialDocumentSearch[K] | undefined): FinancialDocumentSearch => {
    const next: Record<string, unknown> = { ...props.search };
    if (value === undefined) delete next[key];
    else next[key] = value;
    return next as FinancialDocumentSearch;
  };

  if (accountId === null) {
    return (
      <section className="op-page">
        <FinanceHeader outcome={null} />
        <Block>
          <BlockNote>{open.loading ? portalText("finance", "m0454") : FINANCIAL_DOCUMENT_COPY.chooseOrganization}</BlockNote>
        </Block>
      </section>
    );
  }

  const busy = download.isPending ? (download.variables?.financialDocumentId ?? null) : resend.isPending ? (resend.variables?.financialDocumentId ?? null) : null;
  const workspaces = profile.data?.workspace ?? [];

  return (
    <section className="op-page">
      <FinanceHeader outcome={outcome} />
      {canRead ? <LegalProfileForm accountId={accountId} organizationName={organizationName} /> : null}
      {canRead ? (
        <div className="fd-filters" role="group" aria-label={portalText("finance", "m0455")}>
          <Select aria-label={portalText("finance", "m0443")} value={period ?? ""} onChange={(event) => onSearch(withChange("period", event.target.value === "" ? undefined : event.target.value))}>
            <option value="">{portalText("finance", "m0456")}</option>
            {recentMonthsOf(new Date()).map((one) => (
              <option key={one} value={one}>
                {monthWordOf(one)}
              </option>
            ))}
          </Select>
          <Select aria-label={portalText("finance", "m0389")} value={workspaceId ?? ""} onChange={(event) => onSearch(withChange("workspace", event.target.value === "" ? undefined : event.target.value))}>
            <option value="">{portalText("finance", "m0457")}</option>
            {workspaces.map((one) => (
              <option key={one.workspaceId} value={one.workspaceId}>
                {one.name}
              </option>
            ))}
          </Select>
          <Select aria-label={portalText("finance", "m0298")} value={billingAccountId ?? ""} onChange={(event) => onSearch(withChange("balance", event.target.value === "" ? undefined : event.target.value))}>
            <option value="">{portalText("finance", "m0458")}</option>
            {(balances.data ?? []).map((one) => (
              <option key={one.billingAccountId} value={one.billingAccountId}>
                {one.displayName ?? portalText("finance", "m0298")}
              </option>
            ))}
          </Select>
          {filtered ? (
            <button type="button" className="op-link" onClick={() => onSearch({})}>
              {FINANCIAL_DOCUMENT_COPY.clear}
            </button>
          ) : null}
        </div>
      ) : null}
      <Block
        title={FINANCIAL_DOCUMENT_COPY.tableTitle}
        buttons={
          canRead ? (
            <span className="fd-head-actions">
              <Select aria-label={portalText("finance", "m0442")} value={kind ?? ""} onChange={(event) => onSearch(withChange("kind", KIND_SEARCH.find((one) => one === event.target.value)))}>
                {KIND_OPTION.map((one) => (
                  <option key={one.value} value={one.value}>
                    {one.label}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={portalText("finance", "m0459")}
                value={paymentState ?? ""}
                onChange={(event) => onSearch(withChange("payment", PAYMENT_SEARCH.find((one) => one === event.target.value)))}
              >
                {PAYMENT_OPTION.map((one) => (
                  <option key={one.value} value={one.value}>
                    {one.label}
                  </option>
                ))}
              </Select>
              <Button variant="secondary" disabled={exported.isPending} onClick={() => exported.mutate()}>
                <Icon glyph={GLYPH.downloadSimple} size="md" />
                <span>{portalText("finance", "m0287")}</span>
              </Button>
            </span>
          ) : undefined
        }
      >
        {!canRead && page.isError ? (
          <BlockNote>{FINANCIAL_DOCUMENT_COPY.noAccess}</BlockNote>
        ) : page.isPending ? (
          <BlockNote>{portalText("finance", "m0460")}</BlockNote>
        ) : page.isError ? (
          <BlockNote>{apiProblemText(page.error, portalText("finance", "m0461"))}</BlockNote>
        ) : (
          <>
            {page.data.issuance === "withheld" ? (
              <Notice tone="neutral" glyph={GLYPH.fileText} title={FINANCIAL_DOCUMENT_COPY.withheldTitle} text={FINANCIAL_DOCUMENT_COPY.withheld} />
            ) : null}
            {page.data.document.length === 0 && page.data.estimated.length === 0 ? (
              <BlockNote>{filtered ? portalText("finance", "m0462") : FINANCIAL_DOCUMENT_COPY.empty}</BlockNote>
            ) : (
              <DocumentTable
                document={page.data.document}
                estimated={page.data.estimated}
                canResend={canManage}
                busy={busy}
                onDownload={(one) => download.mutate(one)}
                onResend={(one) => resend.mutate(one)}
              />
            )}
            {cursor.length > 1 || page.data.nextCursor !== null ? (
              <div className="fd-pager">
                {cursor.length > 1 ? (
                  <button type="button" className="op-link" onClick={() => setCursor(cursor.slice(0, -1))}>{portalText("finance", "m0463")}</button>
                ) : null}
                {page.data.nextCursor !== null ? (
                  <button type="button" className="op-link" onClick={() => setCursor([...cursor, page.data.nextCursor])}>{portalText("finance", "m0301")}</button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Block>
      {canRead ? <FinanceOverviewBlock accountId={accountId} filter={{ period, workspaceId, billingAccountId }} /> : null}
    </section>
  );
}
